"use client";

import { Button, Input, Select } from "@tosize/ui";
import { type ChangeEvent, type ComponentProps, type ReactNode, useState } from "react";
import { useFormContext } from "react-hook-form";
import type { ContactOrdersType } from "~/app/actions/customer";
import { useQuery } from "~/hooks/useQuery";
import type { HemlockContactFormType } from "../../Form";
import type { HemlockIssueType } from "../../types/output";

interface OrderInputClientProps {
	customerOrderItems?: NonNullable<NonNullable<ContactOrdersType>["orders"]>["items"];
	messages: {
		email: {
			label: string;
			description: string;
			invalidLabel: string;
		};
		incrementId: {
			label: string;
			showSelectLabel: string;
			hideSelectLabel: string;
		};
	};
}

export function OrderInputClient(props: OrderInputClientProps) {
	const { customerOrderItems, messages } = props;
	const { resetField, setValue } = useFormContext<HemlockContactFormType>();
	const { createUrl, router, searchParams } = useQuery();
	const emailParam = searchParams.get("email") ?? undefined;
	const incrementIdParam = searchParams.get("increment_id") ?? undefined;
	const defaultShowSelect =
		emailParam && incrementIdParam
			? customerOrderItems?.find(
					(order) => order.email === emailParam && order.incrementId === incrementIdParam,
				)
			: !!customerOrderItems?.length;
	const [showSelect, setShowSelect] = useState(defaultShowSelect);

	function onShowChange() {
		setShowSelect(!showSelect);

		const defaultValues = !showSelect
			? {
					email: customerOrderItems?.[0]?.email ?? undefined,
					incrementId: customerOrderItems?.[0]?.incrementId,
				}
			: undefined;

		resetField("email", { defaultValue: defaultValues?.email ?? "" });
		resetField("increment_id", { defaultValue: defaultValues?.incrementId ?? "" });

		const url = createUrl({
			email: defaultValues?.email ?? null,
			increment_id: defaultValues?.incrementId ?? null,
		});

		router.push(url, { scroll: false });
	}

	function onSelectChange(value: string) {
		const newOrder = customerOrderItems?.find((order) => order.incrementId === value);

		if (newOrder?.email) {
			setValue("email", newOrder.email);
			setValue("increment_id", newOrder.incrementId);

			const url = createUrl({
				email: newOrder?.email ?? null,
				increment_id: newOrder?.incrementId ?? null,
			});

			router.push(url, { scroll: false });
		}
	}

	return (
		<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:last:odd:*:col-span-full">
			<div>
				{showSelect ? (
					<Select
						label={messages.incrementId.label}
						name="increment_id"
						registerOptions={{
							value: customerOrderItems?.[0]?.incrementId,
							onChange: (e: ChangeEvent<HTMLInputElement>) => onSelectChange(e.target.value),
						}}
					>
						{customerOrderItems?.map((item) => (
							<option value={item.incrementId} key={item.incrementId}>
								{item.incrementId}
							</option>
						))}
					</Select>
				) : (
					<Input
						label={messages.incrementId.label}
						name="increment_id"
						registerOptions={{
							onChange: (e: ChangeEvent<HTMLInputElement>) => {
								const url = createUrl({ increment_id: e.target.value ?? null });

								router.push(url, { scroll: false });
							},
						}}
					/>
				)}

				{defaultShowSelect && (
					<Button variant="link" className="text-sm" onClick={() => onShowChange()}>
						{showSelect
							? messages.incrementId.showSelectLabel
							: messages.incrementId.hideSelectLabel}
					</Button>
				)}
			</div>
			<Input
				label={messages.email.label}
				description={messages.email.description}
				type="email"
				autoComplete="email"
				name="email"
				registerOptions={{
					pattern: {
						value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
						message: messages.email.invalidLabel,
					},
					onChange: (e: ChangeEvent<HTMLInputElement>) => {
						const url = createUrl({ email: e.target.value ?? null });

						router.push(url, { scroll: false });
					},
				}}
			/>
		</div>
	);
}

interface ToggleFieldProps {
	showLabel: string;
	hideLabel?: string;
}

export function ToggleField(props: ToggleFieldProps & ComponentProps<typeof Button>) {
	const { showLabel, hideLabel, children, ...restProps } = props;
	const [show, setShow] = useState(false);

	return (
		<>
			{((show && !!hideLabel) || !show) && (
				<Button onClick={() => setShow((prev) => !prev)} {...restProps}>
					{show ? hideLabel : showLabel}
				</Button>
			)}

			{show && children}
		</>
	);
}

interface Props {
	index: number;
	types: HemlockIssueType[];
	inverted?: boolean;
	children: ReactNode;
}

export function ShowIssueTypeClient({ index, types, inverted = false, children }: Props) {
	const { watch } = useFormContext<HemlockContactFormType>();
	const issueTypeValue = watch(`product.${index}.issue.type`);

	if (inverted) {
		if (typeof issueTypeValue !== "string" || !issueTypeValue || types.includes(issueTypeValue)) {
			return null;
		}

		return children;
	}

	if (typeof issueTypeValue !== "string" || !types.includes(issueTypeValue)) {
		return null;
	}

	return children;
}
